/**
 * Iter 220 — Inbox Unifiée Messagerie Meta.
 * Route : /admin/messagerie
 *
 * Layout 3 colonnes façon WhatsApp Web :
 *   ┌─────────┬────────────────────┬──────────────┐
 *   │ Threads │ Conversation       │ Contexte IA  │
 *   │ (sidebar│ (bulles + reply)   │ (classifica- │
 *   │  liste) │                    │  tion + expé)│
 *   └─────────┴────────────────────┴──────────────┘
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  MessageCircle, Send, Sparkles, Bot, User as UserIcon, RefreshCw,
  Inbox, Check, CheckCheck, Clock, AlertCircle, Circle, ChevronRight,
  UserPlus, Archive, Ban, CheckCircle2, Activity,
} from "lucide-react";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const CHANNELS = [
  { value: "", label: "Tous", color: "#94a3b8" },
  { value: "whatsapp", label: "WhatsApp", color: "#25d366" },
  { value: "messenger", label: "Messenger", color: "#0084ff" },
  { value: "instagram", label: "Instagram", color: "#e1306c" },
];

const ASSIGNMENT_FILTERS = [
  { value: "", label: "Toutes" },
  { value: "me", label: "Mes conversations" },
  { value: "unassigned", label: "Non assignées" },
];

const STATUS_OPTIONS = [
  { value: "open", label: "Ouverte", color: "#22c55e", icon: Circle },
  { value: "closed", label: "Résolue", color: "#64748b", icon: CheckCircle2 },
  { value: "spam", label: "Spam", color: "#f43f5e", icon: Ban },
];

const CHANNEL_STYLES = {
  whatsapp: { bg: "#25d366", label: "WA", icon: "💬" },
  messenger: { bg: "#0084ff", label: "FB", icon: "💙" },
  instagram: { bg: "#e1306c", label: "IG", icon: "📸" },
};

const AI_CATEGORY_COLORS = {
  faq_simple: "#22c55e",
  technical_question: "#3b82f6",
  complaint: "#f43f5e",
  qualified_prospect: "#f59e0b",
  spam: "#64748b",
  other: "#94a3b8",
};

const AI_CATEGORY_LABELS = {
  faq_simple: "FAQ simple",
  technical_question: "Question technique",
  complaint: "Réclamation",
  qualified_prospect: "Prospect qualifié",
  spam: "Spam",
  other: "Autre",
};

function formatRelTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "à l'instant";
    if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
    if (diff < 7 * 86400) return `il y a ${Math.floor(diff / 86400)} j`;
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
  } catch {
    return "";
  }
}

function formatTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

export default function UnifiedInboxPage() {
  const { axiosAuth, user: currentUser } = useAuth();
  const api = useMemo(() => axiosAuth(), [axiosAuth]);
  const [threads, setThreads] = useState([]);
  const [channelFilter, setChannelFilter] = useState("");
  const [assignmentFilter, setAssignmentFilter] = useState("");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [threadDetail, setThreadDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [members, setMembers] = useState([]);
  const [showAssignMenu, setShowAssignMenu] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [showTemplateMenu, setShowTemplateMenu] = useState(false);
  const [templateFilter, setTemplateFilter] = useState("");
  const messagesEndRef = useRef(null);

  const loadTemplates = useCallback(async () => {
    try {
      const r = await api.get("/meta/templates");
      setTemplates(r.data?.templates || []);
    } catch { setTemplates([]); }
  }, [api]);

  const loadMembers = useCallback(async () => {
    try {
      const r = await api.get("/meta/assignable-members");
      setMembers(r.data?.members || []);
    } catch {
      setMembers([]);
    }
  }, [api]);

  const loadThreads = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (channelFilter) params.set("channel", channelFilter);
      if (assignmentFilter) params.set("assigned_to", assignmentFilter);
      if (unreadOnly) params.set("unread_only", "true");
      params.set("limit", "100");
      const r = await api.get(`/meta/threads?${params.toString()}`);
      setThreads(r.data?.threads || []);
    } catch {
      setThreads([]);
    } finally {
      setLoading(false);
    }
  }, [api, channelFilter, assignmentFilter, unreadOnly]);

  const loadThreadDetail = useCallback(async (threadId) => {
    if (!threadId) { setThreadDetail(null); return; }
    setDetailLoading(true);
    try {
      const r = await api.get(`/meta/threads/${threadId}`);
      setThreadDetail(r.data);
      // Mark read
      try { await api.post(`/meta/threads/${threadId}/mark-read`); } catch { /* ignore */ }
    } catch {
      setThreadDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, [api]);

  useEffect(() => { loadThreads(); }, [loadThreads]);
  useEffect(() => { loadMembers(); }, [loadMembers]);
  useEffect(() => { loadTemplates(); }, [loadTemplates]);
  useEffect(() => { loadThreadDetail(selectedId); }, [selectedId, loadThreadDetail]);

  // Auto-scroll
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [threadDetail]);

  const sendReply = async () => {
    if (!replyText.trim() || !selectedId || sending) return;
    setSending(true);
    try {
      await api.post(`/meta/threads/${selectedId}/reply`, { text: replyText.trim(), source: "human" });
      setReplyText("");
      await loadThreadDetail(selectedId);
      await loadThreads();
    } finally {
      setSending(false);
    }
  };

  const regenerateSuggestion = async (messageId) => {
    setSuggesting(true);
    try {
      await api.post(`/meta/messages/${messageId}/ai-suggest`);
      await loadThreadDetail(selectedId);
    } finally {
      setSuggesting(false);
    }
  };

  const approveSuggestion = async (messageId) => {
    if (!window.confirm("Envoyer la suggestion IA au destinataire ?")) return;
    try {
      await api.post(`/meta/messages/${messageId}/ai-approve`);
      await loadThreadDetail(selectedId);
      await loadThreads();
    } catch (e) {
      alert("Envoi impossible : " + (e?.response?.data?.detail || e.message));
    }
  };

  const assignThread = async (userId) => {
    if (!selectedId) return;
    setShowAssignMenu(false);
    try {
      await api.put(`/meta/threads/${selectedId}/assign`, { user_id: userId });
      await loadThreadDetail(selectedId);
      await loadThreads();
    } catch (e) {
      alert("Assignation impossible : " + (e?.response?.data?.detail || e.message));
    }
  };

  const updateStatus = async (newStatus) => {
    if (!selectedId) return;
    if (newStatus === "spam" && !window.confirm("Marquer cette conversation comme SPAM ?")) return;
    try {
      await api.put(`/meta/threads/${selectedId}/status`, { status: newStatus });
      await loadThreadDetail(selectedId);
      await loadThreads();
    } catch (e) {
      alert("Mise à jour impossible : " + (e?.response?.data?.detail || e.message));
    }
  };

  // Iter 226 — Templates rapides /cmd
  const handleReplyChange = (val) => {
    setReplyText(val);
    const match = val.match(/^\/([a-z0-9_-]*)$/i);
    if (match) {
      setTemplateFilter(match[1].toLowerCase());
      setShowTemplateMenu(true);
    } else {
      setShowTemplateMenu(false);
    }
  };

  const insertTemplate = async (tpl) => {
    if (!selectedId) return;
    try {
      const r = await api.post(`/meta/templates/${tpl.template_id}/render`, { thread_id: selectedId });
      setReplyText(r.data?.rendered || tpl.body);
      setShowTemplateMenu(false);
      api.post(`/meta/templates/${tpl.template_id}/use`).catch(() => {});
    } catch {
      setReplyText(tpl.body);
      setShowTemplateMenu(false);
    }
  };

  const filteredTemplates = useMemo(() => {
    const f = (templateFilter || "").toLowerCase();
    return templates.filter((t) => {
      if (!f) return true;
      const sh = (t.shortcut || "").replace(/^\//, "").toLowerCase();
      const ti = (t.title || "").toLowerCase();
      return sh.includes(f) || ti.includes(f);
    }).slice(0, 6);
  }, [templates, templateFilter]);

  const lastInboundWithAi = useMemo(() => {
    if (!threadDetail?.messages) return null;
    const inbound = threadDetail.messages.filter((m) => m.direction === "inbound" && m.ai_classification);
    return inbound.length ? inbound[inbound.length - 1] : null;
  }, [threadDetail]);

  return (
    <div className="p-4 max-w-[1600px] mx-auto h-[calc(100vh-80px)] flex flex-col" data-testid="unified-inbox-page">
      <PageHeader
        title="Messagerie Unifiée"
        subtitle="WhatsApp · Messenger · Instagram Direct — centralisé avec agent IA mixte (classification + suggestions de réponse)."
        icon={<Inbox />}
        actions={
          <button
            onClick={() => window.location.href = "/admin/messagerie/stats"}
            className="text-[11px] px-3 py-1.5 rounded border border-white/10 text-slate-300 hover:bg-white/5 inline-flex items-center gap-1"
            data-testid="open-stats-btn"
          >
            <Activity className="w-3 h-3" /> Stats
          </button>
        }
      />

      <div className="flex-1 grid grid-cols-1 md:grid-cols-[320px_1fr_340px] gap-3 overflow-hidden mt-2">
        {/* ═══ COLONNE 1 — THREADS ═══ */}
        <div className="bg-[#0b0811] border border-white/5 rounded-xl flex flex-col overflow-hidden" data-testid="inbox-threads-col">
          <div className="p-3 border-b border-white/5 space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-white flex-1">Conversations ({threads.length})</span>
              <button onClick={loadThreads} className="text-slate-500 hover:text-white" title="Rafraîchir" data-testid="btn-refresh-threads">
                <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
              </button>
            </div>
            <div className="flex gap-1 flex-wrap">
              {CHANNELS.map((c) => (
                <button
                  key={c.value || "all"}
                  onClick={() => setChannelFilter(c.value)}
                  className={`text-[10px] px-2 py-0.5 rounded-full border transition-colors ${channelFilter === c.value ? "border-violet-500 text-white bg-violet-500/15" : "border-white/10 text-slate-400 hover:text-white"}`}
                  data-testid={`filter-channel-${c.value || "all"}`}
                >
                  {c.label}
                </button>
              ))}
            </div>
            <div className="flex gap-1 flex-wrap">
              {ASSIGNMENT_FILTERS.map((f) => (
                <button
                  key={f.value || "any"}
                  onClick={() => setAssignmentFilter(f.value)}
                  className={`text-[10px] px-2 py-0.5 rounded-full border transition-colors ${assignmentFilter === f.value ? "border-amber-500 text-amber-200 bg-amber-500/15" : "border-white/10 text-slate-500 hover:text-slate-300"}`}
                  data-testid={`filter-assign-${f.value || "any"}`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <label className="flex items-center gap-2 text-[11px] text-slate-400 cursor-pointer">
              <input type="checkbox" checked={unreadOnly} onChange={(e) => setUnreadOnly(e.target.checked)} className="accent-violet-500" data-testid="filter-unread" />
              Non lus seulement
            </label>
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center text-slate-500 text-xs">Chargement…</div>
            ) : threads.length === 0 ? (
              <div className="p-8 text-center" data-testid="inbox-empty">
                <MessageCircle className="w-8 h-8 mx-auto mb-2 text-slate-700" />
                <p className="text-xs text-slate-500">Aucune conversation.</p>
                <p className="text-[10px] text-slate-600 mt-1">Les messages reçus apparaîtront ici.</p>
              </div>
            ) : (
              threads.map((t) => {
                const style = CHANNEL_STYLES[t.channel] || {};
                const isSelected = t.thread_id === selectedId;
                return (
                  <button
                    key={t.thread_id}
                    onClick={() => setSelectedId(t.thread_id)}
                    className={`w-full text-left p-3 border-b border-white/5 hover:bg-white/5 transition-colors ${isSelected ? "bg-violet-500/10 border-l-2 border-l-violet-500" : ""}`}
                    data-testid={`thread-item-${t.thread_id}`}
                  >
                    <div className="flex items-start gap-2.5">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ backgroundColor: style.bg || "#64748b" }}>
                        {(t.sender_name || t.sender_id || "?").charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs font-bold text-white truncate">{t.sender_name || t.sender_id}</span>
                          <span className="text-[9px] text-slate-500 flex-shrink-0">{formatRelTime(t.last_message_at)}</span>
                        </div>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span className="text-[9px] px-1 rounded font-bold" style={{ color: style.bg, backgroundColor: `${style.bg}22` }}>{style.label}</span>
                          {t.last_message_direction === "outbound" && <CheckCheck className="w-2.5 h-2.5 text-slate-500" />}
                          <span className="text-[10px] text-slate-400 truncate">{t.last_message_preview || "(pièce jointe)"}</span>
                        </div>
                        {(t.assigned_to_name || t.status === "closed" || t.status === "spam") && (
                          <div className="flex items-center gap-1 mt-1">
                            {t.assigned_to_name && (
                              <span className="text-[9px] inline-flex items-center gap-0.5 px-1 py-0.5 rounded bg-blue-500/10 text-blue-300" data-testid={`thread-assignee-${t.thread_id}`}>
                                <UserPlus className="w-2 h-2" /> {t.assigned_to_name}
                              </span>
                            )}
                            {t.status && t.status !== "open" && <ThreadStatusPill status={t.status} />}
                          </div>
                        )}
                      </div>
                      {t.unread_count > 0 && (
                        <span className="bg-violet-500 text-white text-[9px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-1">{t.unread_count}</span>
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* ═══ COLONNE 2 — CONVERSATION ═══ */}
        <div className="bg-[#0b0811] border border-white/5 rounded-xl flex flex-col overflow-hidden" data-testid="inbox-chat-col">
          {!selectedId ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <MessageCircle className="w-12 h-12 mx-auto mb-3 text-slate-700" />
                <p className="text-sm text-slate-400">Sélectionnez une conversation</p>
                <p className="text-[11px] text-slate-500 mt-1">pour afficher les messages et répondre.</p>
              </div>
            </div>
          ) : detailLoading || !threadDetail ? (
            <div className="flex-1 flex items-center justify-center"><span className="text-xs text-slate-500">Chargement…</span></div>
          ) : (
            <>
              {/* Header */}
              <div className="p-3 border-b border-white/5 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0" style={{ backgroundColor: CHANNEL_STYLES[threadDetail.thread.channel]?.bg || "#64748b" }}>
                  {(threadDetail.thread.sender_name || threadDetail.thread.sender_id || "?").charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-white truncate">{threadDetail.thread.sender_name || threadDetail.thread.sender_id}</div>
                  <div className="text-[10px] text-slate-500 capitalize flex items-center gap-2">
                    <span>{threadDetail.thread.channel} · {threadDetail.messages.length} message(s)</span>
                    <ThreadStatusPill status={threadDetail.thread.status} />
                  </div>
                </div>
                {/* Assign dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setShowAssignMenu((v) => !v)}
                    className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-white/10 text-slate-300 hover:bg-white/5"
                    data-testid="btn-assign-toggle"
                  >
                    <UserPlus className="w-3 h-3" />
                    {threadDetail.thread.assigned_to_name ? (
                      <span className="max-w-[120px] truncate">{threadDetail.thread.assigned_to_name}</span>
                    ) : (
                      <span>Assigner</span>
                    )}
                  </button>
                  {showAssignMenu && (
                    <div className="absolute right-0 top-full mt-1 bg-[#14101c] border border-white/10 rounded-lg shadow-xl z-20 min-w-[200px] max-h-64 overflow-y-auto" data-testid="assign-menu">
                      <button
                        onClick={() => assignThread(null)}
                        className="w-full text-left px-3 py-2 text-[11px] text-slate-300 hover:bg-white/5 border-b border-white/5"
                        data-testid="assign-none"
                      >
                        — Non assigné
                      </button>
                      {members.length === 0 ? (
                        <div className="px-3 py-2 text-[11px] text-slate-500">Aucun membre</div>
                      ) : (
                        members.map((m) => (
                          <button
                            key={m.user_id}
                            onClick={() => assignThread(m.user_id)}
                            className={`w-full text-left px-3 py-2 text-[11px] hover:bg-white/5 ${threadDetail.thread.assigned_to === m.user_id ? "bg-violet-500/15 text-violet-200" : "text-slate-300"}`}
                            data-testid={`assign-member-${m.user_id}`}
                          >
                            <div className="font-medium truncate">{m.name}</div>
                            <div className="text-[10px] text-slate-500 truncate">{m.email}</div>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
                {/* Status buttons */}
                <div className="flex gap-1">
                  {threadDetail.thread.status !== "closed" && (
                    <button onClick={() => updateStatus("closed")} className="p-1.5 rounded border border-white/10 text-emerald-400 hover:bg-emerald-500/10" title="Marquer résolu" data-testid="btn-status-closed">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {threadDetail.thread.status === "closed" && (
                    <button onClick={() => updateStatus("open")} className="p-1.5 rounded border border-white/10 text-slate-300 hover:bg-white/5" title="Réouvrir" data-testid="btn-status-open">
                      <Circle className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {threadDetail.thread.status !== "spam" && (
                    <button onClick={() => updateStatus("spam")} className="p-1.5 rounded border border-white/10 text-rose-400 hover:bg-rose-500/10" title="Marquer comme spam" data-testid="btn-status-spam">
                      <Ban className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {threadDetail.messages.map((m) => (
                  <MessageBubble key={m.message_id} m={m} />
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Reply */}
              <div className="p-3 border-t border-white/5 relative">
                {showTemplateMenu && filteredTemplates.length > 0 && (
                  <div className="absolute left-3 right-3 bottom-full mb-1 bg-[#14101c] border border-violet-500/30 rounded-lg shadow-xl max-h-64 overflow-y-auto z-30" data-testid="template-menu">
                    <div className="px-3 py-1.5 text-[9px] uppercase font-bold text-slate-500 border-b border-white/5">
                      ⚡ Templates {templateFilter && `(filtre : /${templateFilter})`}
                    </div>
                    {filteredTemplates.map((t) => (
                      <button
                        key={t.template_id}
                        onClick={() => insertTemplate(t)}
                        className="w-full text-left px-3 py-2 hover:bg-violet-500/10 border-b border-white/5"
                        data-testid={`template-pick-${t.template_id}`}
                      >
                        <div className="flex items-center gap-2">
                          {t.shortcut && <code className="text-[10px] px-1 rounded bg-amber-500/15 text-amber-300">{t.shortcut}</code>}
                          <span className="text-xs font-bold text-white">{t.title}</span>
                        </div>
                        <p className="text-[10px] text-slate-400 truncate mt-0.5">{t.body.slice(0, 80)}…</p>
                      </button>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <Input
                    value={replyText}
                    onChange={(e) => handleReplyChange(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), sendReply())}
                    placeholder="Votre réponse… (tapez /cmd pour insérer un template)"
                    className="bg-[#0d0a14] border-white/10 text-white text-sm"
                    data-testid="reply-input"
                  />
                  <Button onClick={sendReply} disabled={!replyText.trim() || sending} className="bg-violet-500 hover:bg-violet-600 text-white" data-testid="btn-send-reply">
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-[10px] text-slate-600 mt-1.5">↵ Entrée pour envoyer · /cmd pour les templates · {templates.length} template(s) disponible(s)</p>
              </div>
            </>
          )}
        </div>

        {/* ═══ COLONNE 3 — CONTEXTE IA ═══ */}
        <div className="bg-[#0b0811] border border-white/5 rounded-xl flex flex-col overflow-hidden" data-testid="inbox-ai-col">
          <div className="p-3 border-b border-white/5 flex items-center gap-2">
            <Bot className="w-4 h-4 text-violet-400" />
            <span className="text-xs font-bold text-white">Assistant IA</span>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {!selectedId ? (
              <p className="text-[11px] text-slate-500">Sélectionnez une conversation pour voir la classification IA et les suggestions.</p>
            ) : !lastInboundWithAi ? (
              <p className="text-[11px] text-slate-500">Pas encore de classification IA pour ce thread. L'IA analyse chaque message entrant dans la seconde qui suit sa réception.</p>
            ) : (
              <AiContextPanel
                message={lastInboundWithAi}
                onRegenerate={() => regenerateSuggestion(lastInboundWithAi.message_id)}
                onApprove={() => approveSuggestion(lastInboundWithAi.message_id)}
                suggesting={suggesting}
              />
            )}

            {selectedId && threadDetail?.thread && (
              <div className="pt-4 border-t border-white/5 space-y-2">
                <h4 className="text-[10px] uppercase font-bold text-slate-500">Détails expéditeur</h4>
                <dl className="space-y-1 text-[11px]">
                  <InfoRow label="ID" value={threadDetail.thread.sender_id} />
                  <InfoRow label="Canal" value={threadDetail.thread.channel} />
                  <InfoRow label="Ouvert depuis" value={formatRelTime(threadDetail.thread.first_message_at)} />
                  <InfoRow label="Statut" value={threadDetail.thread.status} />
                </dl>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function MessageBubble({ m }) {
  const isInbound = m.direction === "inbound";
  const style = CHANNEL_STYLES[m.channel] || {};
  const sourceLabel = m.source === "ai_auto" ? "IA auto" : m.source === "ai_approved" ? "IA validée" : null;
  return (
    <div className={`flex ${isInbound ? "justify-start" : "justify-end"}`}>
      <div className="max-w-[75%]">
        <div className={`rounded-2xl px-3.5 py-2 text-xs ${isInbound ? "bg-[#1a1425] text-white rounded-bl-sm" : "text-white rounded-br-sm"}`} style={!isInbound ? { backgroundColor: style.bg || "#7c3aed" } : {}}>
          <p className="whitespace-pre-wrap break-words">{m.text || <em className="text-slate-400">(pièce jointe)</em>}</p>
        </div>
        <div className={`flex items-center gap-1.5 mt-1 text-[9px] text-slate-500 ${isInbound ? "justify-start" : "justify-end"}`}>
          <span>{formatTime(m.created_at)}</span>
          {!isInbound && (
            <>
              {m.sent ? <CheckCheck className="w-2.5 h-2.5 text-emerald-400" /> : m.error ? <AlertCircle className="w-2.5 h-2.5 text-rose-400" title={m.error} /> : <Clock className="w-2.5 h-2.5 text-slate-500" />}
              {sourceLabel && <span className="px-1 rounded bg-violet-500/20 text-violet-300">{sourceLabel}</span>}
            </>
          )}
          {isInbound && m.ai_auto_replied && <span className="px-1 rounded bg-emerald-500/20 text-emerald-400">IA a répondu</span>}
        </div>
      </div>
    </div>
  );
}

function AiContextPanel({ message, onRegenerate, onApprove, suggesting }) {
  const c = message.ai_classification || {};
  const color = AI_CATEGORY_COLORS[c.category] || "#94a3b8";
  const label = AI_CATEGORY_LABELS[c.category] || "Autre";
  const confidencePct = Math.round((c.confidence || 0) * 100);
  return (
    <div className="space-y-3" data-testid="ai-context-panel">
      <div>
        <h4 className="text-[10px] uppercase font-bold text-slate-500 mb-1.5">Classification</h4>
        <div className="bg-[#14101c] rounded-lg p-3 border-l-2" style={{ borderColor: color }}>
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-bold" style={{ color }}>{label}</span>
            <span className="text-[10px] text-slate-400">{confidencePct}%</span>
          </div>
          <div className="w-full h-1 bg-white/5 rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all" style={{ width: `${confidencePct}%`, backgroundColor: color }} />
          </div>
          {c.requires_human_review && (
            <p className="text-[10px] text-amber-400 mt-2 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" /> Révision humaine requise
            </p>
          )}
          {c.model && <p className="text-[9px] text-slate-600 mt-2">Modèle : {c.model}</p>}
        </div>
      </div>

      {c.suggested_response && (
        <div>
          <h4 className="text-[10px] uppercase font-bold text-slate-500 mb-1.5">Réponse suggérée</h4>
          <div className="bg-gradient-to-br from-violet-500/10 to-blue-500/5 border border-violet-500/30 rounded-lg p-3">
            <p className="text-xs text-white whitespace-pre-wrap">{c.suggested_response}</p>
            <div className="flex gap-1.5 mt-3">
              <Button onClick={onApprove} disabled={!message.tenant_id} size="sm" className="bg-violet-500 hover:bg-violet-600 text-white text-[11px] h-7 px-2" data-testid="btn-ai-approve">
                <CheckCheck className="w-3 h-3 mr-1" /> Approuver & envoyer
              </Button>
              <Button onClick={onRegenerate} disabled={suggesting} size="sm" variant="outline" className="border-white/10 text-white hover:bg-white/5 text-[11px] h-7 px-2" data-testid="btn-ai-regenerate">
                {suggesting ? <RefreshCw className="w-3 h-3 animate-spin" /> : <><Sparkles className="w-3 h-3 mr-1" /> Régénérer</>}
              </Button>
            </div>
          </div>
        </div>
      )}

      {c.error && (
        <div className="bg-rose-500/10 border border-rose-500/30 rounded-lg p-2 text-[10px] text-rose-400">
          Erreur classification : {c.error}
        </div>
      )}
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex justify-between gap-2">
      <dt className="text-slate-500">{label}</dt>
      <dd className="text-slate-300 truncate text-right">{value || "—"}</dd>
    </div>
  );
}

function ThreadStatusPill({ status }) {
  const opt = STATUS_OPTIONS.find((s) => s.value === status) || STATUS_OPTIONS[0];
  const Icon = opt.icon;
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase" style={{ backgroundColor: `${opt.color}1f`, color: opt.color }}>
      <Icon className="w-2.5 h-2.5" /> {opt.label}
    </span>
  );
}
